"use client";

import { useRef } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { StoreCardData } from "@/types/store";
import { getStoreLogoAlt } from "@/lib/storeLogo";

interface StoresDirectoryProps {
  popularStores: StoreCardData[];
  recentlyUpdatedStores: StoreCardData[];
  popularAndRecentlyAdded: StoreCardData[];
}

export default function StoresDirectory({
  popularStores,
  recentlyUpdatedStores,
  popularAndRecentlyAdded,
}: StoresDirectoryProps) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll function for navigation buttons
  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.offsetWidth * 0.8; // Scroll 80% of container width
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Helper to get image URL
  const getImageUrl = (image: string | undefined) => {
    if (!image) return "/placeholder-store.png";
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    return `https://www.itscoupons.com${image.startsWith("/") ? image : `/${image}`}`;
  };

  // Use stores that are both popular and recently added for "Recently Added & Trending"
  const displayRecentlyUpdated = popularAndRecentlyAdded.slice(0, 6);
  const displayPopular = popularStores.slice(0, 12);

  return (
    <div className="w-full">
      {/* Recently Added & Trending Stores Section */}
      <section className="mb-16 bg-gradient-to-br from-purple-100 via-purple-50 to-purple-100 py-8 px-4 md:px-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-8">
          Recently Added & Trending Stores
        </h2>
        
        {/* Mobile Navigation Buttons - Left Side Only */}
        <div className="flex justify-start gap-2 mb-4 md:hidden">
          <button
            onClick={() => scroll("left")}
            aria-label="Scroll left"
            className="bg-white shadow-md rounded-full p-2 hover:bg-purple-100 focus:outline-none"
          >
            <ChevronLeft className="w-5 h-5 text-purple-700" />
          </button>
          <button
            onClick={() => scroll("right")}
            aria-label="Scroll right"
            className="bg-white shadow-md rounded-full p-2 hover:bg-purple-100 focus:outline-none"
          >
            <ChevronRight className="w-5 h-5 text-purple-700" />
          </button>
        </div>

        {/* Scrollable Container - Hide scrollbar */}
        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto pb-4 justify-start md:justify-center scroll-smooth no-scrollbar"
        >
            {displayRecentlyUpdated.map((store) => (
              <Link
                key={store._id}
                href={`/stores/${store.slug}`}
                className="flex-shrink-0 flex flex-col items-center justify-center bg-white rounded-lg border border-gray-200 p-3 md:p-4 hover:shadow-lg transition-shadow w-[calc((100vw-64px)/3)] md:w-[178px]"
                style={{ height: "178px" }}
              >
                <div className="w-full h-[120px] flex items-center justify-center bg-white mb-3">
                  <img
                    src={getImageUrl(store.image)}
                    alt={getStoreLogoAlt(store.slug)}
                    title={`${store.name} Coupons & Deals`}
                    loading="lazy"
                    width={120}
                    height={120}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder-store.png";
                    }}
                  />
                </div>
                <div className="text-sm font-semibold text-purple-800 text-center">
                  {store.name}
                </div>
              </Link>
            ))}
        </div>
        </div>
      </section>

      {/* Top Merchants Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <section className="mb-16 pl-4 md:pl-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
          Top Merchants
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[0, 1, 2].map((colIndex) => {
            const storesInColumn = displayPopular.filter(
              (_, index) => index % 3 === colIndex
            );
            
            return (
              <ul key={colIndex} className="space-y-2">
                {storesInColumn.map((store) => (
                  <li key={store._id}>
                    <Link
                      href={`/stores/${store.slug}`}
                      className="text-gray-900 hover:text-purple-700 text-lg transition-colors"
                    >
                      {store.name} Coupons
                    </Link>
                  </li>
                ))}
              </ul>
            );
          })}
        </div>
      </section>

      {/* Recently Added Section */}
      <section className="mb-16 pl-4 md:pl-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
          Recently Added
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[0, 1, 2].map((colIndex) => {
            const storesInColumn = recentlyUpdatedStores.filter(
              (_, index) => index % 3 === colIndex
            );
            
            return (
              <ul key={colIndex} className="space-y-2">
                {storesInColumn.map((store) => (
                  <li key={store._id}>
                    <Link
                      href={`/stores/${store.slug}`}
                      className="text-gray-900 hover:text-purple-700 text-lg transition-colors"
                    >
                      {store.name} Coupons
                    </Link>
                  </li>
                ))}
              </ul>
            );
          })}
        </div>
      </section>

      {/* Alphabetical Navigation */}
      <section className="border-t border-gray-200 pt-8">
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {alphabet.map((letter) => (
            <Link
              key={letter}
              href={`/stores?letter=${letter.toLowerCase()}`}
              className="text-purple-700 hover:text-purple-900 font-medium text-lg transition-colors"
            >
              {letter}
            </Link>
          ))}
          <Link
            href="/stores?letter=0-9"
            className="text-purple-700 hover:text-purple-900 font-medium text-lg transition-colors"
          >
            #
          </Link>
        </div>
      </section>
      </div>
    </div>
  );
}
